import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";

const VitalsMonitorList = ({ records = [], onAdd, onEdit, onDelete, loading }) => {
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter Logic
  const filteredRecords = records.filter((r) =>
    Object.values(r).some((v) =>
      String(v).toLowerCase().includes(filterText.toLowerCase())
    )
  );

  const totalRows = filteredRecords.length;
  const start = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  // Delete Confirmation
  const handleDeleteClick = (id) => {
    if (onDelete) onDelete(id);
  };

  // Export PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Vitals Monitoring Chart", 14, 10);
    const tableColumn = ["#", "Name", "DOB", "Approved By", "Effective Date"];
    const tableRows = filteredRecords.map((r, i) => [
      i + 1,
      r.name,
      r.dob,
      r.approvedBy,
      r.effectiveDate,
    ]);
    autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
    doc.save("vitals_monitoring.pdf");
  };

  // Custom Pagination
  const CustomPagination = ({ onChangePage }) => {
    const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">Rows per page:</span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 15, 20].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-sm border"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="mx-3 text-muted small">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-sm border"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  // Table Columns
  const columns = [
    {
      name: "S.No",
      cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
      width: "70px",
    },
    { name: "Name", selector: (r) => r.name, sortable: true },
    { name: "DOB", selector: (r) => r.dob },
    { name: "Approved By", selector: (r) => r.approved_by },
    { name: "Effective Date", selector: (r) => r.effective_date },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex justify-content-center gap-1">
          {onEdit && (
            <button
              className="btn btn-sm btn-outline-primary"
              title="Edit"
              onClick={() => onEdit(row.id)}
            >
              <i className="ri-edit-line"></i>
            </button>
          )}
          {onDelete && (
            <button
              className="btn btn-sm btn-outline-danger"
              title="Delete"
              onClick={() => handleDeleteClick(row.id)}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
        </div>
      ),
      width: "130px",
      ignoreRowClick: true,
      button: true,
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        {/* Left: Export + Search */}
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown">
            <button
              className="btn btn-label-primary btn-sm dropdown-toggle"
              data-bs-toggle="dropdown"
            >
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li>
                <button className="dropdown-item" onClick={handleExportPDF}>
                  <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                </button>
              </li>
              <li>
                <CSVLink
                  data={filteredRecords}
                  filename="vitals_monitoring.csv"
                  className="dropdown-item"
                >
                  <i className="ri-file-excel-line me-1 text-success"></i> CSV
                </CSVLink>
              </li>
            </ul>
          </div>

          <input
            type="text"
            placeholder="Search records..."
            className="form-control form-control-sm"
            style={{ width: "260px" }}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        {/* Center Info */}
        <h6 className="mb-0 text-center text-muted flex-grow-1">
          Showing {start}-{end} of {totalRows} records
        </h6>

        {/* Right: Add Button */}
        {onAdd && (
          <div>
            <button className="btn btn-sm btn-primary" onClick={onAdd}>
              <i className="ri-add-line me-1"></i> Add New
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card-body p-2">
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={filteredRecords}
            pagination
            paginationPerPage={rowsPerPage}
            paginationTotalRows={totalRows}
            onChangeRowsPerPage={setRowsPerPage}
            onChangePage={setCurrentPage}
            paginationComponent={CustomPagination}
            highlightOnHover
            striped
            responsive
            noDataComponent={<div className="p-3 text-muted">No records found</div>}
          />
        )}
      </div>
    </div>
  );
};

export default VitalsMonitorList;
